import React, { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { Loading, PageHeader, EmptyState } from "@/components/common";
import { Landmark, ExternalLink, Building2, Scale, Wallet, Gavel, FileText, Search as SearchIcon } from "lucide-react";

const GROUP_ICON = { legislature: Landmark, governor: FileText, court: Scale, agencies: Building2, rec: Wallet, budget: Gavel };
const HEALTH_STYLE = {
  "HEALTHY": "bg-brand-teal-50 text-brand-teal border-brand-teal/30",
  "DEGRADED": "bg-amber-50 text-amber-700 border-amber-300",
  "REFERENCE-ONLY": "bg-slate-100 text-slate-600 border-slate-300",
  "DISABLED": "bg-slate-100 text-slate-400 border-slate-200",
};

export default function Hub() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/hub/overview").then((r) => setData(r.data)).catch(() => setData(false)); }, []);

  if (data === null) return <Loading />;
  if (!data) return <EmptyState icon={Landmark} title="Unable to load Hub" />;

  const filterItems = (items) => q ? items.filter((it) => (`${it.title} ${it.summary} ${it.source}`).toLowerCase().includes(q.toLowerCase())) : items;

  return (
    <div>
      <PageHeader title="Government Information Hub" subtitle="Unified access to Florida legislative and executive-branch information. Source health is shown honestly — degraded and reference-only sources are never hidden." />
      <div className="relative mb-6 max-w-md">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="hub-search" placeholder="Filter items in all groups…"
          className="w-full h-10 rounded-lg bg-white border border-brand-line pl-10 pr-3 text-sm text-brand-ink outline-none focus:border-brand-civic" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="hub-groups">
        {data.groups.map((g) => {
          const Icon = GROUP_ICON[g.key] || Landmark;
          const items = filterItems(g.items);
          const refConn = g.health.connectors.find((c) => c.reference_url);
          return (
            <div key={g.key} className="rounded-xl border border-brand-line bg-white shadow-card overflow-hidden" data-testid={`hub-group-${g.key}`}>
              <div className="flex items-center justify-between gap-2 p-4 border-b border-brand-line bg-slate-50/50">
                <div className="flex items-center gap-2">
                  <span className="h-7 w-7 rounded-md bg-brand-navy flex items-center justify-center"><Icon className="h-4 w-4 text-white" /></span>
                  <h3 className="font-display font-semibold text-brand-ink">{g.label}</h3>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border font-bold uppercase ${HEALTH_STYLE[g.health.state] || HEALTH_STYLE.DISABLED}`} data-testid={`hub-health-${g.key}`}>{g.health.state}</span>
              </div>

              {(g.health.state === "REFERENCE-ONLY" || g.health.state === "DEGRADED" || g.health.state === "DISABLED") && (
                <div className="px-4 py-3 text-xs text-slate-500 bg-amber-50/40 border-b border-brand-line" data-testid={`hub-note-${g.key}`}>
                  Automated extraction is <b>{g.health.state.toLowerCase()}</b> for this source. Use the official reference below.
                  {refConn?.reference_url && <a href={refConn.reference_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-brand-civic ml-1 font-medium">Official source <ExternalLink className="h-3 w-3" /></a>}
                </div>
              )}

              <div className="divide-y divide-brand-line max-h-80 overflow-y-auto">
                {items.length === 0 ? (
                  <p className="p-4 text-sm text-slate-400">{g.health.state === "HEALTHY" ? "No items match your filter." : "No automated items — see official reference above."}</p>
                ) : items.map((it) => (
                  <div key={it.item_id} className="p-4" data-testid={`hub-item-${it.item_id}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] uppercase tracking-wider font-bold text-brand-gold">{it.category}</span>
                      <span className="text-[10px] text-slate-400">{fmtDate(it.published_date)}</span>
                    </div>
                    <h4 className="font-medium text-brand-ink text-sm leading-snug">{it.title}</h4>
                    {it.summary && <p className="text-xs text-slate-500 mt-1">{it.summary}</p>}
                    <div className="flex items-center justify-between mt-2 text-xs text-slate-400">
                      <span>{it.source}{it.retrieved_at ? ` · retrieved ${fmtDate(it.retrieved_at)}` : ""}</span>
                      {it.source_url && <a href={it.source_url.startsWith("http") ? it.source_url : `https://${it.source_url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-brand-civic"><ExternalLink className="h-3 w-3" /> Source</a>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
